package org.teddapp.project.ui

import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Constraints
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.sp

/**
 * Un titre qui s'adapte à la place qu'on lui laisse, sur tous les écrans.
 *
 * Il part de la taille de [style] et la réduit par paliers jusqu'à ce que deux choses
 * soient vraies : aucun mot n'est coupé en deux (« dimension / ner »), et le texte
 * tient en [maxLines] lignes. Il ne descend pas sous [minFontSize] ; s'il déborde
 * encore, il finit par des points de suspension. L'interligne suit la taille.
 *
 * À utiliser pour tout titre dont le texte vient de l'utilisateur ou dont la largeur
 * dépend de la fenêtre : nom d'une compétence, d'une pièce, salutation, titre d'écran.
 */
@Composable
fun TeddFitText(
    text: String,
    style: TextStyle,
    modifier: Modifier = Modifier,
    color: Color = TeddTokens.Ink,
    minFontSize: TextUnit = 18.sp,
    maxLines: Int = 3,
) {
    val measurer = rememberTextMeasurer()
    BoxWithConstraints(modifier) {
        // La largeur se lit par tranches de seize pixels : tirer une poignée ne remesure
        // pas le titre à chaque pixel, et ce qui tient dans la tranche tient dans la place.
        val maxWidth = (constraints.maxWidth / WIDTH_STEP) * WIDTH_STEP
        val cache = remember(text, style, minFontSize, maxLines) { HashMap<Int, TextStyle>() }
        val fitted = if (constraints.maxWidth == Constraints.Infinity || maxWidth <= 0) style else cache.getOrPut(maxWidth) {
            TeddTrace.hit("fittext.compute")
            val top = style.fontSize.value
            val floor = minFontSize.value.coerceAtMost(top)
            val ratio = if (style.lineHeight.isSp && top > 0f) style.lineHeight.value / top else 1.15f
            val words = text.split(' ', '\n', ' ').filter { it.isNotBlank() }
            val longest = words.maxByOrNull { it.length }.orEmpty()
            var size = top
            var chosen = style
            while (true) {
                val candidate = style.copy(fontSize = size.sp, lineHeight = (size * ratio).sp)
                chosen = candidate
                val wordFits = longest.isEmpty() ||
                    measurer.measure(longest, candidate, softWrap = false, maxLines = 1).size.width <= maxWidth
                val lines = if (wordFits) measurer.measure(text, candidate, constraints = Constraints(maxWidth = maxWidth)).lineCount else Int.MAX_VALUE
                if ((wordFits && lines <= maxLines) || size <= floor) break
                // Paliers de 2 sp au-dessus de 28, de 1 sp en dessous : assez fin pour l'œil.
                size = (size - if (size > 28f) 2f else 1f).coerceAtLeast(floor)
            }
            chosen
        }
        Text(text = text, style = fitted, color = color, maxLines = maxLines, overflow = TextOverflow.Ellipsis)
    }
}

/** Tranche de largeur, en pixels, en deçà de laquelle un titre n'est pas remesuré. */
private const val WIDTH_STEP = 16
